import { useMemo, type ReactNode } from "react";
import { CssBaseline, GlobalStyles } from "@mui/material";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  type BreakpointsOptions,
  type SimplePaletteColorOptions,
} from "@mui/material/styles";
import { useAppSelector } from "@/store";

import { BREAKPOINT_VALUES, SHAPE } from "@/constants/layout";
import {
  getContrastColor,
  getDarkColorShade,
  getLightColorShade,
  isValidHexColor,
  normalizeHexColor,
} from "@/utils/color";
import componentsOverrides from "./components";
import { darkPalette, lightPalette } from "./palette";
import { darkShadows, lightShadows } from "./shadows";
import typography from "./typography";

const breakpoints = {
  values: BREAKPOINT_VALUES,
} as unknown as BreakpointsOptions;

/** The four shades MUI needs; empty when the tenant has no brand colour set. */
interface BrandPrimary {
  light?: string;
  main?: string;
  dark?: string;
  contrastText?: string;
}

/**
 * Derives the MUI `primary` colour from the tenant's `brand_color`.
 * Falls back to the `?brand_color=` query parameter (used by the
 * back-office preview) and finally to the default palette.
 *
 * The original applies NO legibility clamp: the four shades come straight off
 * `brand_color`. An earlier pass here lifted the colour until it cleared AA
 * against the surface, which changed how every brand renders. That has been
 * removed so the palette is pixel-identical to the original — where the
 * original's own contrast falls short of WCAG it is recorded in
 * RECOVERY_PLAN.md rather than silently corrected here.
 */
const useBrandPrimary = (brandColor: string | undefined): BrandPrimary =>
  useMemo(() => {
    let color = brandColor;

    if (!color?.startsWith("#")) {
      if (typeof window === "undefined") return {};
      const fromQuery = new URLSearchParams(window.location.search).get(
        "brand_color",
      );
      const candidate = fromQuery != null ? `#${fromQuery.trim()}` : null;
      if (!candidate || !isValidHexColor(candidate)) return {};
      color = candidate;
    }

    const base = normalizeHexColor(color);

    return {
      light: getLightColorShade(base, 20),
      main: base,
      dark: getDarkColorShade(base, 20),
      contrastText: getContrastColor(base),
    };
  }, [brandColor]);

interface ThemeProviderProps {
  children?: ReactNode;
}

export default function ThemeProvider({ children }: ThemeProviderProps) {
  const { brand_color: brandColor, dark_mode: darkMode } = useAppSelector(
    (state) => state.settings.data,
  );

  // `?dark_mode=1` previews the dark palette without changing the tenant's
  // stored setting, mirroring how `?brand_color=` works.
  const isDarkMode = useMemo(() => {
    if (typeof window === "undefined") return Boolean(darkMode);
    const fromQuery = new URLSearchParams(window.location.search).get(
      "dark_mode",
    );
    if (fromQuery === "1" || fromQuery === "true") return true;
    if (fromQuery === "0" || fromQuery === "false") return false;
    return Boolean(darkMode);
  }, [darkMode]);

  const primary = useBrandPrimary(brandColor);

  const theme = useMemo(() => {
    const basePalette = isDarkMode ? darkPalette : lightPalette;
    const next = createTheme({
      palette: {
        ...basePalette,
        primary: {
          ...(basePalette.primary as SimplePaletteColorOptions),
          ...primary,
        },
      },
      typography,
      shape: SHAPE,
      breakpoints,
      shadows: isDarkMode ? darkShadows : lightShadows,
    });
    next.components = componentsOverrides(isDarkMode) as typeof next.components;
    return next;
  }, [isDarkMode, primary]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {/*
        Placeholder art is drawn on a light neutral so it sits well on the
        default surface. On the dark theme that reads as a bright patch, so
        knock it back rather than shipping a second set of assets.
      */}
      <GlobalStyles
        styles={{
          'img[data-placeholder="true"]': {
            opacity: isDarkMode ? 0.35 : 1,
            filter: isDarkMode ? "grayscale(0.4)" : "none",
          },
        }}
      />
      {children}
    </MuiThemeProvider>
  );
}
